<template>
  <div class="us-input-google-geo">
    <div class="us-input-google-geo__wrapper">
      <component
        :is="headingLevel"
        v-if="heading"
        class="display--d2--semibold us-input-google-geo__heading-wrapper"
      >
        {{ heading }}
      </component>
      <slot name="heading" />
      <div>
        <input
          ref="input"
          v-model="currentLoc"
          type="text"
          class="us-input-google-geo__input"
          aria-label="Enter Address - Use the up and down arrow keys to navigate address suggestions."
          @focus="clearCurrentLoc"
        />
        <div class="us-input-google-geo__current-loc-button-wrapper">
          <button
            v-if="!loading"
            ref="currentLocButton"
            type="button"
            tabindex="0"
            aria-disabled="false"
            aria-label="Use your current location"
            role="button"
            class="us-input-google-geo__current-loc-button"
            @click="fetchCurrentLoc"
          >
            <USIcon name="map-pin" css-diameter="16px" />
          </button>
          <USIcon
            v-else
            name="spinner"
            css-diameter="22px"
            class="us-input-google-geo__loading"
          />
        </div>
      </div>
    </div>
    <p v-if="error && false">{{ error }}</p>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue'
import { Loader } from '@googlemaps/js-api-loader'

import USIcon from '@base/components/USIcon/USIcon.vue'

export default defineComponent({
  name: 'USInputGoogleGeolocation',
  components: { USIcon },
  props: {
    apiKey: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: 'ca'
    },
    loc: {
      type: String,
      default: ''
    },
    heading: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: String as PropType<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>,
      default: 'p'
    }
  },
  emits: ['coordinates'],
  async setup(props, { emit }) {
    const input = ref<HTMLInputElement | undefined>(undefined)
    const currentLocButton = ref<HTMLButtonElement | undefined>(undefined)
    const error = ref<string>('')
    let loading = ref<boolean>(false)
    const currentLoc = ref(props.loc)

    onMounted(async () => {
      // Google Autocomplete
      const loader = new Loader({
        apiKey: props.apiKey || '',
        version: 'weekly',
        libraries: ['places']
      })
      try {
        const google = await loader.load()
        const autocomplete = new google.maps.places.Autocomplete(input.value, {
          fields: ['geometry.location'],
          componentRestrictions: {
            country: props.country
          },
          types: ['geocode']
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          const lat = place?.geometry?.location?.lat()
          const lon = place?.geometry?.location?.lng()

          if (lat !== undefined && lon !== undefined) {
            if (input.value) currentLoc.value = input.value.value
            emit('coordinates', { lat, lon, loc: currentLoc.value })
          } else {
            error.value = 'Could not find'
          }
        })
      } catch (e) {
        console.error(e)
      }
    })

    // Current Loc Button
    let currentLon: number | undefined
    let currentLat: number | undefined
    const currentLocationInputValue = 'Current Location'

    const fetchCurrentLoc = (e: MouseEvent) => {
      e.preventDefault()
      if (loading.value) return

      loading.value = true

      if (currentLon !== undefined && currentLat !== undefined) {
        currentLoc.value = currentLocationInputValue
        emit('coordinates', {
          lat: currentLat,
          lon: currentLon,
          loc: currentLoc.value
        })
        loading.value = false
        return
      }

      if (
        navigator.geolocation &&
        (currentLon === undefined || currentLat === undefined)
      ) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            currentLat = position.coords.latitude
            currentLon = position.coords.longitude
            currentLoc.value = currentLocationInputValue
            emit('coordinates', {
              lat: currentLat,
              lon: currentLon,
              loc: currentLoc.value
            })
            loading.value = false
          },
          () => {
            error.value = 'user declined'
            loading.value = false
          }
        )
      } else {
        error.value = 'browser does not support geolocation'
        loading.value = false
      }
    }

    const clearCurrentLoc = () => {
      if (currentLoc.value === currentLocationInputValue) {
        currentLoc.value = ''
      }
    }

    return {
      input,
      currentLocButton,
      error,
      loading,
      currentLoc,
      fetchCurrentLoc,
      clearCurrentLoc
    }
  }
})
</script>

<style lang="scss" scoped>
.us-input-google-geo {
  &__input {
    padding: var(--spacing--7);
    width: 100%;

    &::placeholder {
      color: var(--color--neutral-a1);
      opacity: 1;
    }
  }

  &__current-loc-button-wrapper {
    height: 26px;
    position: absolute;
    right: var(--spacing--8);
    top: 50%;
    transform: translateY(-50%);
  }

  &__loading {
    animation: rotation 1s infinite ease-in-out; // TODO update motion
  }

  &__heading-wrapper {
    color: var(--color--neutral-a1) !important;
    font-weight: var(--font-weight--medium) !important;
    margin-bottom: var(--spacing--8);
  }
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

:deep(svg) {
  color: var(--color--primary-b1);
}
</style>

<style lang="scss">
.pac-container {
  background-color: var(--color--neutral-a5);
  border: 0;
  border-radius: var(--border-radius--small);
  box-shadow: none;
  color: var(--color--neutral-a1);

  &:after {
    content: none;
  }
}

.pac-item {
  border-top: 0;
  padding: 15px;

  &:hover,
  &-selected {
    background-color: var(--color--primary-b3);
    color: var(--color--neutral-a5);

    &,
    .pac-item-query,
    .pac-matched {
      color: var(--color--neutral-a1);
    }
  }
}

.pac-item,
.pac-item-query,
.pac-matched {
  color: var(--color--neutral-a1);
  font-size: var(--font-size--z7);
  font-weight: var(--font-weight--regular);
}

.pac-icon {
  display: none;
}
</style>
